/**
 * Build the structured `reference` payload that travels with an
 * "Ask Curiescious" turn — the user's selected text plus its
 * provenance (source doc + citation tokens detected in the excerpt).
 *
 * Selected text is UNTRUSTED user input pulled out of arbitrary
 * documents (PDFs, papers, web pages). It will be passed into an LLM
 * prompt alongside the user's question, so this util closes the easy
 * prompt-injection vectors at the client boundary. The backend
 * re-sanitises with the same rules — defense in depth.
 *
 * Sanitisation:
 *  - cap length (default 4 000 chars)
 *  - strip zero-width / invisible Unicode smuggling chars
 *  - strip well-known model role/control tokens (Anthropic, OpenAI,
 *    Llama, etc.) so the excerpt cannot impersonate a system turn
 *  - strip ANSI escape sequences
 *  - escape any literal `</reference_excerpt>` so the excerpt cannot
 *    close the delimiter the backend wraps it in
 *  - collapse runs of whitespace into single spaces (keeps the chip
 *    readable and prevents whitespace-based smuggling)
 */

/**
 * Compact citation-token shape carried through the slot from the
 * BlockSuite-land toolbar. We deliberately drop the `raw` field that
 * `CitationToken` in `../types` keeps for editor-side rendering — the
 * downstream `OrchestrationReference` only needs `type` + `identifier`,
 * and aligning the shape here lets the slot pass its payload through
 * without a redundant remap.
 */
export interface ReferenceCitationToken {
  type: 'pubmed' | 'doi' | 'arxiv';
  identifier: string;
}

export interface ReferencePayload {
  text: string;
  sourceDocId?: string;
  citationTokens?: ReferenceCitationToken[];
  /** True when the original exceeded the length cap and was truncated. */
  truncated: boolean;
  /** Original length before sanitisation (for telemetry / debug). */
  originalLength: number;
}

const DEFAULT_MAX_CHARS = 4000;

// Zero-width / invisible chars commonly used to smuggle instructions.
// U+200B ZWSP, U+200C ZWNJ, U+200D ZWJ, U+FEFF BOM, U+2060 WJ,
// U+180E MVS, U+00AD SHY, U+034F CGJ
// eslint-disable-next-line no-misleading-character-class
const INVISIBLE_CHARS = /[\u200B-\u200D\uFEFF\u2060\u180E\u00AD\u034F]/g;

// ANSI escape sequences (some terminal/log-based attacks).
const ANSI_ESCAPES = /\x1B\[[0-9;]*[A-Za-z]/g;

// Model-specific role/control tokens. The list is the union of common
// chat-template markers — strip them all regardless of which provider
// we end up routing the prompt through.
const CONTROL_TOKENS: RegExp[] = [
  /<\|im_start\|>/gi,
  /<\|im_end\|>/gi,
  /<\|system\|>/gi,
  /<\|user\|>/gi,
  /<\|assistant\|>/gi,
  /<\|endoftext\|>/gi,
  /<\|fim_prefix\|>/gi,
  /<\|fim_suffix\|>/gi,
  /<\|fim_middle\|>/gi,
  /\[INST\]/gi,
  /\[\/INST\]/gi,
  /<<SYS>>/gi,
  /<<\/SYS>>/gi,
  /<\|begin_of_text\|>/gi,
  /<\|end_of_text\|>/gi,
  /<\|start_header_id\|>/gi,
  /<\|end_header_id\|>/gi,
  /<\|eot_id\|>/gi,
  // Anthropic legacy markers — the literal sequence "\n\nHuman:" /
  // "\n\nAssistant:" used to demarcate turns in older completion-style
  // calls. Strip case-insensitively to be safe.
  /\n\nHuman:/gi,
  /\n\nAssistant:/gi,
];

function stripControlTokens(input: string): string {
  let out = input;
  for (const re of CONTROL_TOKENS) out = out.replace(re, '');
  return out;
}

function escapeDelimiter(input: string): string {
  // Insert a zero-width space inside any literal closing tag so the
  // excerpt cannot close the <reference_excerpt> wrapper the backend
  // applies. We strip ZWSP earlier in the pipeline, but for the
  // FINAL escape we re-insert it intentionally as a one-char break.
  return input.replace(
    /<\/reference_excerpt>/gi,
    '</reference\u200B_excerpt>'
  );
}

export interface BuildReferenceOptions {
  maxChars?: number;
  sourceDocId?: string;
  citationTokens?: ReferenceCitationToken[];
}

/**
 * Sanitise selected text and package it as a structured reference
 * payload. Returns `null` when the input is empty after sanitisation.
 */
export function buildReferencePayload(
  rawText: string,
  options: BuildReferenceOptions = {}
): ReferencePayload | null {
  const { maxChars = DEFAULT_MAX_CHARS, sourceDocId, citationTokens } = options;

  if (!rawText) return null;
  const originalLength = rawText.length;

  let text = rawText;
  text = text.replace(INVISIBLE_CHARS, '');
  text = text.replace(ANSI_ESCAPES, '');
  text = stripControlTokens(text);
  // Normalise newlines + collapse whitespace runs. Keeps single newlines
  // so paragraph structure survives.
  text = text.replace(/\r\n?/g, '\n').replace(/[ \t]+/g, ' ');
  text = text.trim();

  let truncated = false;
  if (text.length > maxChars) {
    text = `${text.slice(0, maxChars).trimEnd()}\u2026 [truncated]`;
    truncated = true;
  }

  text = escapeDelimiter(text);
  if (!text) return null;

  return {
    text,
    sourceDocId,
    citationTokens,
    truncated,
    originalLength,
  };
}
